import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from "react-select";
import { TextField } from '@mui/material';

import api from '@/services/api';
import Alert from '@/views/components/Alert/Alert';

import styles from '@/views/pages/TemplatesManagement/TemplatesMetricsList.module.scss';

const TemplatesMetricsList = () => {
	const { dict } = useSelector((state) => state.app);

	const htmlTypes = [
		{ value: 'input', label: dict.templatesManagement.metrics.textField }, 
		{ value: 'date', label: dict.templatesManagement.metrics.date },
	];

	const getHtmlTypeByValue = (value) => {
		for (let i = 0; i < htmlTypes.length; ++i) {
			if (value === htmlTypes[i].value) {
				return htmlTypes[i];
			}
		}

		return undefined;
	};

	const [ edited, setEdited ] = useState(false);

	const [ defaultSettings, setDefaultSettings ] = useState(null);

	const [ currentSettings, setCurrentSettings ] = useState([]);

	const startEditing = () => {
		setEdited(true);
	};

	const setHtmlTypeForRow = useCallback((code, newValue) => {
		let tempSettings = [];

		for (let i = 0; i < currentSettings.length; ++i) {
			if (currentSettings[i].code === code) {
				tempSettings.push({
					...currentSettings[i],
					htmlType: newValue,
				});
			} else {
				tempSettings.push(currentSettings[i]);
			}
		}

		setCurrentSettings(tempSettings);
	}, [ currentSettings ]);

	const setLabelForRow = (code, newValue) => {
		let tempSettings = [];

		for (let i = 0; i < currentSettings.length; ++i) {
			if (currentSettings[i].code === code) {
				tempSettings.push({
					...currentSettings[i],
					label: newValue,
				});
			} else {
				tempSettings.push(currentSettings[i]);
			}
		}

		setCurrentSettings(tempSettings);
	};

	const setPlaceholderForRow = (code, newValue) => {
		let tempSettings = [];

		for (let i = 0; i < currentSettings.length; ++i) {
			if (currentSettings[i].code === code) {
				tempSettings.push({
					...currentSettings[i],
					placeholder: newValue,
				});
			} else {
				tempSettings.push(currentSettings[i]);
			}
		}

		setCurrentSettings(tempSettings);
	};

	const deleteRow = (code) => {
		let tempSettings = [];

		for (let i = 0; i < currentSettings.length; ++i) {
			if (currentSettings[i].code !== code) {
				tempSettings.push(currentSettings[i]);
			}
		}

		setCurrentSettings(tempSettings);
	};

	const addNewRow = () => {
		setCurrentSettings([ ...currentSettings, { temp: true, code: Math.random(), htmlType: 'input', label: '', placeholder: '' } ]);
	};

	const isSavingBlocked = () => {
		let blocked = false;

		for (let i = 0; i < currentSettings.length; ++i) {
			if (currentSettings[i].label === '' || currentSettings[i].placeholder === ''){
				blocked = true;
				break;
			}
		}

		return blocked;
	};

	const isAlertShown = () => {
		let shown = false;

		if (edited) {
			for (let i = 0; i < currentSettings.length; ++i) {
				if (currentSettings[i].code && (!currentSettings[i].rights.canEdit || !currentSettings[i].rights.canDelete)){
					shown = true;
					break;
				}
			}
		}

		return shown;
	};
	
	const saveChanges = () => {
		setEdited(false);

		let tempSettings = [];

		for (let i = 0; i < currentSettings.length; ++i) {
			if (currentSettings[i].temp) {
				tempSettings.push({ 
					label: currentSettings[i].label,
					placeholder: currentSettings[i].placeholder,
					htmlType: currentSettings[i].htmlType,
				});
			} else {
				tempSettings.push({ 
					code: currentSettings[i].code,
					label: currentSettings[i].label,
					placeholder: currentSettings[i].placeholder,
					htmlType: currentSettings[i].htmlType,
				});
			}
		}

		api.put('/template-field-types/metric', tempSettings).then(() => {
			getMetricFields();
		});
	};

	const cancelChanges = () => {
		setCurrentSettings(defaultSettings);
		setEdited(false);
	};

	const getMetricFields = () => {
		api.get('/template-field-types/metric').then((response) => {
			setDefaultSettings(response.data);
			setCurrentSettings(response.data);
		});
	};

	useEffect(() => {
		getMetricFields();
	}, []);

	return (
		<div className={ styles['templates-metrics-list__wrapper'] }>
			<div className={ styles['templates-metrics-list__top'] }>
				<div className={ styles['templates-metrics-list__top--header'] }>
					{ dict.templatesManagement.metrics.title }
				</div>
				<div className={ styles['templates-metrics-list__top--edit'] }>
					{ !edited && <button 
						className='button__white' 
						onClick={ startEditing }
					>
						{ dict.templatesManagement.metrics.edit }
					</button> }
				</div>
			</div>
			<div className={ styles['templates-metrics-list__container'] }>
				{ isAlertShown() && <Alert
					variant='warning'
					alertContent={ <><strong>Uwaga!</strong> Niektóre metryki zostały już zastosowane w szablonach, dlatego nie ma możliwości ich zmiany lub usunięcia. W dalszym ciągu możesz dodać nowe metryki. </> }
				/> }
				<div className={ styles['templates-metrics-list__list-header'] }>
					<div className={ styles['templates-metrics-list__list-header--type'] }>{ dict.templatesManagement.metrics.type }</div>
					<div className={ styles['templates-metrics-list__list-header--label'] }>{ dict.templatesManagement.metrics.label }</div>
					<div className={ styles['templates-metrics-list__list-header--placeholder'] }>{ dict.templatesManagement.metrics.placeholder }</div>
				</div>
				<div className={ styles['templates-metrics-list__list'] }>
					{ currentSettings.map((value, index) => <div 
						key={ value.code ? value.code : index }
						className={ styles['templates-metrics-list__list--row'] }
					>
						<div className={ styles['templates-metrics-list__list--select'] }>
							<label>{ dict.templatesManagement.metrics.type }</label>
							<Select
								options={ htmlTypes }
								isSearchable={ false }
								components={ {
									IndicatorSeparator: () => null,
								} }
								value={ getHtmlTypeByValue(value.htmlType) }
								onChange={ (el) => { setHtmlTypeForRow(value.code, el.value); } }
								isDisabled={ !edited || value.rights && !value.rights.canEdit }
							/>
						</div>
						<div className={ styles['templates-metrics-list__list--input'] }>
							<label>{ dict.templatesManagement.metrics.label }</label>
							<TextField
								size='small'
								fullWidth
								value={ value.label }
								onChange={ (el) => { setLabelForRow(value.code, el.target.value); } }
								disabled={ !edited || value.rights && !value.rights.canEdit }
								placeholder={ dict.templatesManagement.metrics.labelFieldPlaceholder }
								inputProps={ { maxLength: 512 } }
							/>
						</div>
						<div className={ styles['templates-metrics-list__list--input'] }>
							<label>{ dict.templatesManagement.metrics.placeholder }</label>
							<TextField
								size='small'
								fullWidth
								value={ value.placeholder }
								onChange={ (el) => { setPlaceholderForRow(value.code, el.target.value); } }
								disabled={ !edited || value.rights && !value.rights.canEdit }
								placeholder={ dict.templatesManagement.metrics.placeholderFieldPlaceholder }
								inputProps={ { maxLength: 512 } }
							/>
						</div>
						{ edited && <div className={ styles['templates-metrics-list__list--delete'] }>
							<button 
								className={ `button__transparent${ !value.rights || value.rights.canDelete ? '' : ' disabled'}` }
								onClick={ () => { deleteRow(value.code); } }
							>
								{ dict.templatesManagement.metrics.deleteMetric }
							</button>
						</div> }
					</div>) }
				</div>
				{ edited && <button 
					className='button__transparent'
					onClick={ addNewRow }
				>
					{ dict.templatesManagement.metrics.addMetric }
				</button> }
				{ edited && <div className={ styles['templates-metrics-list__bottom'] }>
					<button 
						className='button__transparent'
						onClick={ cancelChanges }
					>
						{ dict.templatesManagement.metrics.cancel }
					</button>
					<button 
						className='button__blue'
						onClick={ saveChanges }
						disabled={ isSavingBlocked() }
					>
						{ dict.templatesManagement.metrics.saveChanges }
					</button>
				</div> }
			</div>
		</div>
	);
};
export default React.memo(TemplatesMetricsList);